import { useParams } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { PageHeader } from "@/components/shared/page-header";
import { TeamPage } from "@/components/team/TeamPage";
import { Skeleton } from "@/components/ui/skeleton";
import { useMyPerson, usePerson } from "@/hooks/use-persons";
import { useSession } from "@/lib/auth-client";
import { canViewAll } from "@/lib/permissions";

function TeamShellSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/**
 * The viewer's own team. Reached from the People & Org nav item, which appears
 * for any manager or admin — an admin sees it whether or not they have reports,
 * because the company-wide scope is theirs to use. So this has to cope with
 * someone who has no reports, or no person record at all.
 */
export function MyTeamPage() {
  const { data: me, isLoading } = useMyPerson();
  const { data: session } = useSession();

  if (isLoading) return <TeamShellSkeleton />;
  if (!me) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="People & Organization" title="My Team" />
        <EmptyState
          icon={Users}
          title="No person record"
          description="Your login isn't linked to a person record yet, so there's no team to show."
        />
      </div>
    );
  }

  // Admins get the company-wide scope here, and only here.
  return (
    <TeamPage
      managerId={me.id}
      managerName={me.name}
      isSelf
      canSwitchScope={canViewAll(session?.user?.role)}
    />
  );
}

/**
 * A specific manager's team, linked from the Direct reports panel on their
 * profile. The API restricts this to that manager and admins; a viewer who is
 * neither gets the route's error state from the 403.
 */
export function TeamDetailPage() {
  const { personId } = useParams({ from: "/dashboard/team/$personId" });
  const { data: me } = useMyPerson();
  const { data: person, isLoading } = usePerson(personId);

  if (isLoading) return <TeamShellSkeleton />;

  return (
    <TeamPage
      managerId={personId}
      managerName={person?.name ?? null}
      isSelf={me?.id === personId}
    />
  );
}
